import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Badge, Button, Form, Row, Col, Modal, 
  InputGroup, Alert, Spinner, Toast, ToastContainer, Dropdown
} from 'react-bootstrap';
import { 
  Users, Shield, Activity, Search, AlertTriangle, CheckCircle, 
  Lock, Unlock, Ban, Eye, EyeOff, RefreshCw, Plus, Mail, User, Copy, MoreVertical
} from 'lucide-react';
import { 
  getAdminUsers, createUser, updateUserRole, 
  toggleUserStatus, resetUserPassword, getSystemLogs 
} from '../services/admin';

const Admin = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    is_admin: false,
    is_active: true
  });

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAdminUsers();
      
      if (Array.isArray(response)) {
        setUsers(response);
      } else if (response && Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response && Array.isArray(response.users)) {
        setUsers(response.users);
      } else {
        console.error('Unexpected response format:', response);
        setUsers([]);
        showToast('Unexpected response format from server', 'danger');
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showToast('Failed to load users: ' + (error.message || error), 'danger');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch logs from API
  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await getSystemLogs();
      
      if (Array.isArray(response)) {
        setLogs(response);
      } else if (response && Array.isArray(response.data)) {
        setLogs(response.data);
      } else if (response && Array.isArray(response.logs)) {
        setLogs(response.logs);
      } else {
        console.error('Unexpected logs response format:', response);
        setLogs([]);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      showToast('Failed to load logs: ' + (error.message || error), 'danger');
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchLogs();
  }, []);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      await createUser(newUser);
      showToast('User created successfully');
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        is_admin: false,
        is_active: true
      });
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      showToast('Failed to create user: ' + (error.message || error), 'danger');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, { is_admin: newRole === 'admin' });
      showToast('User role updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      showToast('Failed to update role: ' + (error.message || error), 'danger');
    }
  };

  const handleStatusToggle = async (user) => {
    try {
      await toggleUserStatus(user.id, { is_active: !user.is_active });
      showToast(`User ${!user.is_active ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      showToast('Failed to toggle status: ' + (error.message || error), 'danger');
    }
  };

  const handleResetClick = (user) => {
    setUserToReset(user);
    setShowResetModal(true);
    setTempPassword(null);
  };

  const handleResetConfirm = async () => {
    if (userToReset) {
      try {
        setResetting(true);
        const result = await resetUserPassword(userToReset.id);
        
        if (result.temp_password) {
          setTempPassword(result.temp_password);
          showToast('Password reset successfully. Copy the temporary password.');
        } else {
          showToast('Password reset but no temporary password received.', 'warning');
        }
      } catch (error) {
        console.error('Failed to reset password:', error);
        showToast('Failed to reset password: ' + (error.message || error), 'danger');
      } finally {
        setResetting(false);
      }
    }
  };

  const copyToClipboard = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      showToast('Password copied to clipboard!');
      setShowResetModal(false);
      setTempPassword(null);
      setUserToReset(null);
    }
  };

  const getRoleVariant = (isAdmin) => {
    return isAdmin ? 'danger' : 'primary';
  };

  const getStatusVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  const toggleRowExpansion = (userId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(userId)) {
      newExpandedRows.delete(userId);
    } else {
      newExpandedRows.add(userId);
    }
    setExpandedRows(newExpandedRows);
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }) : [];

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading admin panel...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Admin Panel</h1>
          <p className="text-muted mb-0">
            Manage users and monitor system activity
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={fetchLogs} disabled={logsLoading}>
            <RefreshCw size={16} className="me-2" />
            <span className="d-none d-md-inline">Refresh Logs</span>
            <span className="d-md-none">Refresh</span>
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} className="me-2" />
            <span className="d-none d-md-inline">Add User</span>
            <span className="d-md-none">Add</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Users size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{Array.isArray(users) ? users.length : 0}</h5>
              <p className="text-muted mb-0 small">Total Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Shield size={24} className="text-success mb-2" />
              <h5 className="fw-bold mb-0">
                {Array.isArray(users) ? users.filter(u => u.is_active).length : 0}
              </h5>
              <p className="text-muted mb-0 small">Active Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-warning mb-2" style={{ fontSize: '1.2rem' }}>👑</div>
              <h5 className="fw-bold mb-0">
                {Array.isArray(users) ? users.filter(u => u.is_admin).length : 0}
              </h5>
              <p className="text-muted mb-0 small">Admins</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-info mb-2" style={{ fontSize: '1.2rem' }}>📊</div>
              <h5 className="fw-bold mb-0">{Array.isArray(logs) ? logs.length : 0}</h5>
              <p className="text-muted mb-0 small">System Logs</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Management */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-0 py-3">
          <h5 className="fw-bold mb-0">User Management</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {/* Filters */}
          <div className="p-3 border-bottom">
            <Row className="g-2">
              <Col xs={12} md={6}>
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <Search size={14} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search users by email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col xs={12} md={6}>
                <div className="text-muted small d-flex align-items-center h-100 justify-content-end">
                  {filteredUsers.length} users
                </div>
              </Col>
            </Row>
          </div>

          {/* Desktop Users Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 py-3 px-4">Email</th>
                  <th className="border-0 py-3">Role</th>
                  <th className="border-0 py-3">Status</th>
                  <th className="border-0 py-3">Last Login</th>
                  <th className="border-0 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-4 py-3">
                      <div className="fw-medium">{user.email || 'No email'}</div>
                    </td>
                    <td className="py-3">
                      <Form.Select
                        size="sm"
                        value={user.is_admin ? 'admin' : 'user'}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        className="w-auto"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </Form.Select>
                    </td>
                    <td className="py-3">
                      <Badge bg={getStatusVariant(user.is_active)}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <small className="text-muted">{formatDate(user.last_login)}</small>
                    </td>
                    <td className="py-3">
                      <div className="d-flex gap-2">
                        <Button
                          variant={user.is_active ? "outline-warning" : "outline-success"}
                          size="sm"
                          onClick={() => handleStatusToggle(user)}
                          title={user.is_active ? "Deactivate" : "Activate"}
                        >
                          {user.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleResetClick(user)}
                          title="Reset Password"
                        >
                          <Lock size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Users Cards */}
          <div className="d-md-none">
            {filteredUsers.map((user) => {
              const isExpanded = expandedRows.has(user.id);
              
              return (
                <Card key={user.id} className="mb-3 border-0 shadow-sm mx-2">
                  <Card.Body 
                    className="p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRowExpansion(user.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="fw-medium text-truncate">{user.email || 'No email'}</div>
                        <div className="d-flex align-items-center mt-2">
                          <Badge bg={getStatusVariant(user.is_active)} className="me-2">
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge bg={getRoleVariant(user.is_admin)}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </Badge>
                        </div>
                        <div className="text-muted small mt-2">
                          Last Login: {formatDate(user.last_login)}
                        </div>
                      </div>
                      <div>
                        <MoreVertical size={16} />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="row g-2">
                          <div className="col-12">
                            <Form.Select
                              value={user.is_admin ? 'admin' : 'user'}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                            </Form.Select>
                          </div>
                          <div className="col-6">
                            <Button
                              variant={user.is_active ? "outline-warning" : "outline-success"}
                              className="w-100"
                              onClick={() => handleStatusToggle(user)}
                            >
                              {user.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                              <span className="ms-1">{user.is_active ? 'Deactivate' : 'Activate'}</span>
                            </Button>
                          </div>
                          <div className="col-6">
                            <Button
                              variant="outline-info"
                              className="w-100"
                              onClick={() => handleResetClick(user)}
                            >
                              <Lock size={14} />
                              <span className="ms-1">Reset</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-5 text-muted">
              {Array.isArray(users) && users.length === 0 ? 'No users found.' : 
               searchTerm ? 'No users match your search.' : 'No users available.'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* System Logs */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-0 py-3">
          <h5 className="fw-bold mb-0">System Logs</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {logsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading logs...</span>
              </Spinner>
            </div>
          ) : (
            <>
              {/* Desktop Logs Table */}
              <div className="d-none d-md-block table-responsive">
                <Table className="mb-0" hover>
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 py-3 px-4">Timestamp</th>
                      <th className="border-0 py-3">Level</th>
                      <th className="border-0 py-3">Module</th>
                      <th className="border-0 py-3">Message</th>
                      <th className="border-0 py-3">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(logs) && logs.slice(0, 10).map((log) => (
                      <tr key={log.id}>
                        <td className="px-4 py-3">
                          <small className="text-muted">{formatDate(log.created_at)}</small>
                        </td>
                        <td className="py-3">
                          <Badge bg={
                            log.severity === 'error' ? 'danger' : 
                            log.severity === 'warning' ? 'warning' : 
                            log.severity === 'info' ? 'info' : 'secondary'
                          }>
                            {log.severity || 'UNKNOWN'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <small>{log.event_type || 'N/A'}</small>
                        </td>
                        <td className="py-3">
                          <div className="small">{log.message || 'No message'}</div>
                        </td>
                        <td className="py-3">
                          <small className="text-muted">{log.related_id || 'System'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Logs Cards */}
              <div className="d-md-none">
                {Array.isArray(logs) && logs.slice(0, 10).map((log) => (
                  <Card key={log.id} className="mb-2 mx-2 border-0 shadow-sm">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Badge bg={
                          log.severity === 'error' ? 'danger' : 
                          log.severity === 'warning' ? 'warning' : 
                          log.severity === 'info' ? 'info' : 'secondary'
                        }>
                          {log.severity || 'UNKNOWN'}
                        </Badge>
                        <small className="text-muted">{formatDate(log.created_at)}</small>
                      </div>
                      <div className="fw-medium small mb-1">{log.event_type || 'N/A'}</div>
                      <div className="small text-muted mb-2">{log.message || 'No message'}</div>
                      <div className="text-muted small">
                        User: {log.related_id || 'System'}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
          {(!Array.isArray(logs) || logs.length === 0) && !logsLoading && (
            <div className="text-center py-5 text-muted">
              No system logs available.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => { setShowResetModal(false); setTempPassword(null); setUserToReset(null); }} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-info">
            <Lock size={24} className="me-2" />
            {tempPassword ? 'Temporary Password' : 'Reset Password'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {tempPassword ? (
            <div className="text-center">
              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                <CheckCircle size={32} className="text-success" />
              </div>
              <h5 className="fw-bold">Password Reset Successful!</h5>
              <p className="text-muted mb-3">
                Send this temporary password to <strong>{userToReset?.email}</strong>:
              </p>
              
              <div className="bg-light p-3 rounded mb-3">
                <code className="h5 fw-bold text-primary">{tempPassword}</code>
              </div>
              
              <Button variant="success" onClick={copyToClipboard} className="me-2">
                <Copy size={16} className="me-2" />
                Copy to Clipboard
              </Button>
              <Button variant="outline-secondary" onClick={() => { setShowResetModal(false); setTempPassword(null); setUserToReset(null); }}>
                Close
              </Button>
            </div>
          ) : (
            <div className="text-center mb-3">
              <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
                <Lock size={32} className="text-info" />
              </div>
              <h5 className="fw-bold">Reset password for {userToReset?.email}?</h5>
              <p className="text-muted">
                A new temporary password will be generated and displayed here.
              </p>
            </div>
          )}
        </Modal.Body>
        {!tempPassword && (
          <Modal.Footer className="border-top-0 justify-content-center">
            <Button 
              variant="outline-secondary" 
              onClick={() => { setShowResetModal(false); setUserToReset(null); }}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleResetConfirm}
              disabled={resetting}
              className="px-4"
            >
              {resetting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-primary">
            <User size={24} className="me-2" />
            Create New User
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateUser}>
          <Modal.Body className="pt-0">
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Mail size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="email"
                  placeholder="user@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
                minLength={6}
              />
            </Form.Group>

            <Row className="g-3">
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Admin User"
                  checked={newUser.is_admin}
                  onChange={(e) => setNewUser({...newUser, is_admin: e.target.checked})}
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Active User"
                  checked={newUser.is_active}
                  onChange={(e) => setNewUser({...newUser, is_active: e.target.checked})}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={creating}
            >
              {creating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Admin;